import type {
  MontageDimensions,
  MontageLayout,
  WellCultureStatus,
} from '~/api/backend/commonTypes'
import type { PlatePage_WellCulture } from '~/api/backend/pages/platePageAPI'
import {
  isWellImagingObservation,
  type WellPage_Montage,
  type WellPage_WellCulture,
} from '~/api/backend/pages/wellPageAPI'

import { type DemoObjectConverter, LinkingError } from '../demoData'
import { getDemoMontageThumbnails, getDemoPlateSprite } from '../imageSets'
import { utf8ToBase64 } from '../utils/utf8Base64'
import {
  type DemoSourceTimepoint,
  plateObservationKey,
  timestampOrRelativeDay,
} from './demoTimepoint'

export type DemoSourceWell = {
  plateBarcode: string
  position: string
  status: WellCultureStatus

  parentWellPlateBarcode: string
  parentWellPosition: string

  cellLine: string
  cellLineLot: string
  passageNumber: string
}

export type DemoDerivedWell = PlatePage_WellCulture &
  WellPage_WellCulture & {
    cell_concentration?: number | null
    confluence?: {
      value: number
      lastUpdatedAt?: string
      source?: { datasetId: string }
    } | null
  }

type PartialWell = Omit<DemoDerivedWell, 'culturePlate' | 'parentCulture'> & {
  culturePlate: { barcode: string }
  parentCulture: { plateBarcode: string; position: string }
}

export const wellConverter: DemoObjectConverter<
  DemoSourceWell,
  PartialWell,
  DemoDerivedWell
> = {
  tsvColumns: [
    'plateBarcode',
    'position',
    'parentWellPlateBarcode',
    'parentWellPosition',
    'cellLine',
    'cellLineLot',
    'passageNumber',
  ],

  validate(wells) {
    if (
      wells.length !== new Set(wells.map(w => `${w.plateBarcode}|${w.position}`)).size
    ) {
      throw new Error('There are wells with duplicate positions for the same plate.')
    }
    if (wells.some(w => !w.plateBarcode)) {
      throw new Error('Some wells are missing a plate barcode.')
    }
    if (wells.some(w => !w.position)) {
      throw new Error('Some wells are missing a position.')
    }
    if (wells.some(w => w.position !== w.position.toUpperCase())) {
      throw new Error('Positions must be uppercase')
    }
    if (
      wells.some(w => w.passageNumber && Number.isNaN(parseInt(w.passageNumber, 10)))
    ) {
      throw new Error('Some wells have an invalid passage number')
    }
  },

  convert(well, others) {
    const timepoints =
      others.timepoints?.filter(
        t => t.plateBarcode === well.plateBarcode && t.wellPosition === well.position,
      ) ?? []
    const latest = timepoints.length ? timepoints[timepoints.length - 1] : null

    return {
      partialItem: {
        id: encodeURIComponent(utf8ToBase64(`${well.plateBarcode}|${well.position}`)),
        well: well.position,
        name: `${well.plateBarcode}|${well.position}`,
        createdAt: '2024-11-05T17:05:00.000Z',
        // Eventually, we need to thread the actual plate into here.
        culturePlate: { barcode: well.plateBarcode },
        status: 'Active',
        custom_culture_status: {
          id: 'demo-active-status',
          name: 'Active',
          icon: 'ACTIVE',
          type: 'system',
          category: 'active',
          created_at: '2024-01-01T00:00:00.000Z',
          updated_at: '2024-01-01T00:00:00.000Z',
          is_archived: false,
        },
        parentCulture: {
          plateBarcode: well.parentWellPlateBarcode,
          position: well.parentWellPosition,
        },
        cellLine: well.cellLine,
        cellLineLot: well.cellLineLot,
        passageNumber:
          well.passageNumber !== '' && well.passageNumber !== null
            ? parseInt(well.passageNumber, 10)
            : null,
        confluence: latest
          ? {
              lastUpdatedAt: timestampOrRelativeDay(latest.timestamp),
              source: {
                datasetId: plateObservationKey(latest),
              },
              value: parseFloat(latest.confluence),
            }
          : null,
        cell_concentration: null,
        confluenceHistory: timepoints.map(t => ({
          source: {
            datasetId: plateObservationKey(t),
          },
          timestamp: timestampOrRelativeDay(t.timestamp),
          value: parseFloat(t.confluence),
          generated_at: timestampOrRelativeDay(t.timestamp),
        })),
        montage: latest ? timepointToMontage(latest, well.position) : null,
        montages: timepoints.map(t => timepointToMontage(t, well.position)),
        montageHistory: timepoints.map(t => timepointToMontage(t, well.position)),
        observationHistory: timepoints.map(t => ({
          extid: plateObservationKey(t),
          type: 'plate_imaging',
          source: {
            datasetId: plateObservationKey(t),
          },
          timestamp: timestampOrRelativeDay(t.timestamp),
          confluence: parseFloat(t.confluence),
          generated_at: timestampOrRelativeDay(t.timestamp),
          montage: timepointToMontage(t, well.position),
        })),
      },
      lookupKey: demoWellKey(well),
    }
  },

  link(derived, partial, lookup) {
    const barcode = partial.culturePlate.barcode
    const plate = lookup('plates', barcode)
    if (!plate) {
      throw new LinkingError(
        `A well is defined with plate barcode "${barcode}" but there is no plate defined with that barcode.`,
      )
    }
    derived.culturePlate = plate

    derived.parentCulture = lookup('wells', demoWellKey(partial.parentCulture))
    if (
      !derived.parentCulture &&
      (partial.parentCulture.plateBarcode || partial.parentCulture.position)
    ) {
      throw new LinkingError(
        `A well has a parent culture (plate "${partial.parentCulture.plateBarcode}" well "${partial.parentCulture.position}") that does not exist.`,
      )
    }

    derived.createdAt = plate.firstCheckedInAt
    if (!plate.isCheckedIn) {
      derived.status = 'Terminated'
      derived.custom_culture_status = {
        id: 'demo-terminated-status',
        name: 'Terminated',
        icon: 'TERMINATED',
        type: 'system',
        category: 'terminated',
        created_at: '2024-01-01T00:00:00.000Z',
        updated_at: '2024-01-01T00:00:00.000Z',
        is_archived: false,
      }
    }

    // TODO(@nate): Update demo framework to include both plate image and plate read observation histories
    for (const montage of derived.observationHistory
      .filter(isWellImagingObservation)
      .map(obs => obs.montage)) {
      if (montage) {
        montage.culture = derived
      }
    }
  },
}

export function demoWellKey({
  plateBarcode,
  position,
}: {
  plateBarcode: string
  position: string
}): string {
  return `${plateBarcode}|${position}`
}

export function timepointToMontage(
  t: DemoSourceTimepoint,
  wellPos: string,
): WellPage_Montage {
  const thumbs = getDemoMontageThumbnails(t.plateImageSet, wellPos, 2000)
  const { montageLayout } = getDemoPlateSprite(t.plateImageSet, 'SIZE_200X200')
  const images = thumbs.map((url, i) => ({
    id: `${plateObservationKey(t)}_${wellPos}_${i}`,
    imageUrl: url,
    key: url,
    montageId: `${plateObservationKey(t)}_${wellPos}`,
    montageIndex: i,
  }))

  return {
    id: plateObservationKey(t),
    dimensions: MONTAGE_LAYOUT_TO_DIMENSIONS[montageLayout],
    images,
    largeImages: images, // Hack for MontageImagesDialogFragment to work.
    culture: null as unknown as WellPage_WellCulture, // Populate later, during wellConverter.link()
  }
}

const MONTAGE_LAYOUT_TO_DIMENSIONS: { [key in MontageLayout]: MontageDimensions } = {
  SINGLE: { rows: 1, cols: 1 },
  SQUARE_2X2: { rows: 2, cols: 2 },
  SQUARE_3X3: { rows: 3, cols: 3 },
  SQUARE_4X4: { rows: 4, cols: 4 },
}
